import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { features, steps, testimonials, homeFAQ } from '../data/siteData';
import StoreButtons from '../components/StoreButtons';

/* ── Scroll-reveal hook ─────────────────────────────────────────────────────── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.fade-up');
    if (!els.length) return;
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

/* ── SVG Icons ──────────────────────────────────────────────────────────────── */
function BudgetIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  );
}
function ExpenseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
    </svg>
  );
}
function InsightsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  );
}
function IncomeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 19V5"/><polyline points="6 11 12 5 18 11"/>
    </svg>
  );
}
function GoalsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
    </svg>
  );
}
function RemindersIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}
function LockIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}
function ExportIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  );
}
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}
function ChevronDown() {
  return (
    <svg className="faq-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

const iconMap = {
  budget: <BudgetIcon />,
  expense: <ExpenseIcon />,
  income: <IncomeIcon />,
  goals: <GoalsIcon />,
  reminders: <RemindersIcon />,
  insights: <InsightsIcon />,
  lock: <LockIcon />,
  export: <ExportIcon />,
};

/* ── Phone Mockup — Home Screen ─────────────────────────────────────────────── */
function HomeScreenMockup({ extraClass = '' }) {
  const cats = [
    { name: 'Mess', color: '#C8F135', pct: 72, amt: '₹2,160' },
    { name: 'Food Outside', color: '#F97316', pct: 55, amt: '₹825' },
    { name: 'Transport', color: '#3B82F6', pct: 38, amt: '₹304' },
    { name: 'Entertainment', color: '#EC4899', pct: 88, amt: '₹704' },
    { name: 'Savings', color: '#10B981', pct: 100, amt: '₹1,000' },
  ];
  return (
    <div className={`phone-mockup ${extraClass}`}>
      <div className="phone-notch"><div className="phone-notch-speaker"/><div className="phone-notch-camera"/></div>
      <div className="phone-screen">
        <div className="mock-status-bar"><span>9:41</span><span>●●●</span></div>
        <div className="mock-topbar">
          <div><div className="mock-greeting">Good morning,</div><div className="mock-name">Priya 👋</div></div>
          <div className="mock-avatar">P</div>
        </div>
        <div className="mock-donut-section">
          <div className="mock-donut-wrap">
            <div className="mock-donut"/>
            <div className="mock-donut-hole">
              <div className="mock-budget-label">Spent</div>
              <div className="mock-budget-amount">₹4,993</div>
              <div className="mock-budget-sub">of ₹10k</div>
            </div>
          </div>
        </div>
        <div className="mock-categories">
          {cats.map(c => (
            <div className="mock-cat-row" key={c.name}>
              <div className="mock-cat-dot" style={{ background: c.color }}/>
              <div className="mock-cat-name">{c.name}</div>
              <div className="mock-cat-bar-wrap"><div className="mock-cat-bar" style={{ width: `${c.pct}%`, background: c.color }}/></div>
              <div className="mock-cat-amount">{c.amt}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Phone Mockup — Log Expense ─────────────────────────────────────────────── */
function LogExpenseMockup({ extraClass = '' }) {
  const chips = ['Mess', 'Food', 'Transport', 'Hostel', 'Personal'];
  const keys = ['1','2','3','4','5','6','7','8','9','.','0','✓'];
  return (
    <div className={`phone-mockup ${extraClass}`}>
      <div className="phone-notch"><div className="phone-notch-speaker"/><div className="phone-notch-camera"/></div>
      <div className="phone-screen">
        <div className="mock-status-bar"><span>9:41</span><span>●●●</span></div>
        <div className="mock-log-title">Log Expense</div>
        <div className="mock-cat-select">
          {chips.map((c, i) => <div key={c} className={`mock-cat-chip ${i === 1 ? 'active' : ''}`}>{c}</div>)}
        </div>
        <div className="mock-amount-display">
          <span className="mock-amount-prefix">₹</span>
          <span className="mock-amount-value">240</span>
        </div>
        <div className="mock-numpad">
          {keys.map((k, i) => (
            <div key={i} className={`mock-numpad-key ${k === '✓' ? 'mock-numpad-key-lime' : ''}`}>{k}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Phone Mockup — Insights ────────────────────────────────────────────────── */
function InsightsMockup({ extraClass = '' }) {
  const bars = [
    { label: 'Mon', h: 30, active: false },
    { label: 'Tue', h: 55, active: false },
    { label: 'Wed', h: 42, active: false },
    { label: 'Thu', h: 70, active: true },
    { label: 'Fri', h: 25, active: false },
    { label: 'Sat', h: 85, active: false },
    { label: 'Sun', h: 10, active: false },
  ];
  return (
    <div className={`phone-mockup ${extraClass}`}>
      <div className="phone-notch"><div className="phone-notch-speaker"/><div className="phone-notch-camera"/></div>
      <div className="phone-screen">
        <div className="mock-status-bar"><span>9:41</span><span>●●●</span></div>
        <div className="mock-insights-title">This Week</div>
        <div className="mock-bar-chart">
          {bars.map(b => (
            <div className="mock-bar-col" key={b.label}>
              <div className={`mock-bar ${b.active ? 'active' : ''}`} style={{ height: `${b.h}%` }}/>
              <div className="mock-bar-label">{b.label}</div>
            </div>
          ))}
        </div>
        <div className="mock-insight-cards">
          <div className="mock-insight-card">
            <span className="mock-insight-label">Highest spend</span>
            <span className="mock-insight-val">Food Outside</span>
          </div>
          <div className="mock-insight-card">
            <span className="mock-insight-label">Week total</span>
            <span className="mock-insight-val">₹1,840</span>
          </div>
          <div className="mock-insight-card">
            <span className="mock-insight-label">Avg / day</span>
            <span className="mock-insight-val">₹263</span>
          </div>
        </div>
        <div className="mock-streak-section">
          <div className="mock-streak-icon">🔥</div>
          <div><div className="mock-streak-count">7</div><div className="mock-streak-label">Day streak</div></div>
        </div>
      </div>
    </div>
  );
}

/* ── FAQ Item ───────────────────────────────────────────────────────────────── */
function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`}>
      <button className="faq-question" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span>{question}</span>
        <ChevronDown />
      </button>
      <div className="faq-answer">{answer}</div>
    </div>
  );
}

/* ── HomePage ───────────────────────────────────────────────────────────────── */
export default function HomePage() {
  useScrollReveal();

  return (
    <>
      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-bg-gradient"/>
        <div className="hero-bg-gradient-2"/>
        <div className="hero-grid-overlay"/>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-pill-row">
                <div className="pill"><span className="pill-dot-pulse"/>Free Forever</div>
                <div className="pill">Zero Ads</div>
                <div className="pill">iPhone &amp; Android</div>
              </div>
              <h1 className="hero-headline">
                Know Where Every{' '}
                <span className="text-gradient">Rupee</span> Goes.
              </h1>
              <p className="hero-subtext">
                Klar is a budget tracker built for Indian college students. Set your monthly budget, log expenses in seconds, and finally understand your spending — for free, forever. Now live on iPhone and Android.
              </p>
              <div className="hero-ctas">
                <StoreButtons size="lg" />
              </div>
              <div className="hero-secondary-cta">
                <Link to="/product" className="btn btn-ghost btn-sm">
                  See How It Works <ArrowRight />
                </Link>
              </div>
            </div>

            <div className="hero-visual">
              <div className="phone-group">
                <HomeScreenMockup extraClass="phone-main" />
                <LogExpenseMockup extraClass="phone-back-left" />
                <InsightsMockup extraClass="phone-back-right" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats Bar ───────────────────────────────────────────────────── */}
      <div className="stats-bar">
        <div className="container">
          <div className="stats-grid">
            {[
              { number: 'Live', label: 'On iOS & Android' },
              { number: '8', label: 'Budget Categories' },
              { number: '₹0', label: 'Forever Free' },
            ].map((s, i) => (
              <div key={s.label} className={`stat-item fade-up delay-${i * 2 + 1}`}>
                <div className="stat-number">{s.number}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Features ────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="features-intro fade-up">
            <div className="section-label">Features</div>
            <h2 className="text-headline">Everything you need. Nothing you don't.</h2>
            <p className="text-body-lg mt-4">
              Klar strips away the noise and gives you the tools that actually help a college student stay on budget.
            </p>
          </div>
          <div className="features-grid">
            {features.map((f, i) => (
              <div key={f.id} className={`feature-card fade-up delay-${(i % 3) + 1}`}>
                <div className="feature-icon-wrap">{iconMap[f.icon]}</div>
                <h3 className="feature-title">{f.title}</h3>
                <p className="feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ────────────────────────────────────────────────── */}
      <section className="section" style={{ background: 'var(--bg-alt)' }}>
        <div className="container">
          <div className="features-intro fade-up">
            <div className="section-label">How It Works</div>
            <h2 className="text-headline">Up and running in under 3 minutes.</h2>
            <p className="text-body-lg mt-4">
              No bank linking, no complex setup. Just sign in, set a budget, and start logging.
            </p>
          </div>
          <div className="fade-up delay-2">
            <div className="steps-grid" style={{ position: 'relative' }}>
              <div className="steps-line"/>
              {steps.map(s => (
                <div key={s.number} className="step-card">
                  <div className="step-number">{s.number}</div>
                  <h3 className="step-title">{s.title}</h3>
                  <p className="step-desc">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── App Screenshots ──────────────────────────────────────────────── */}
      <section className="section overflow-hidden">
        <div className="container">
          <div className="features-intro fade-up">
            <div className="section-label">The App</div>
            <h2 className="text-headline">Designed for how students actually live.</h2>
            <p className="text-body-lg mt-4">
              Every screen is optimised for speed. Dark by default, readable in any light.
            </p>
          </div>
          <div className="screenshots-grid fade-up delay-2">
            <div className="screenshot-item">
              <HomeScreenMockup />
              <div>
                <div className="screenshot-caption-title">Budget Overview</div>
                <div className="screenshot-caption-sub">Donut ring + category bars</div>
              </div>
            </div>
            <div className="screenshot-item">
              <LogExpenseMockup />
              <div>
                <div className="screenshot-caption-title">Log Expense</div>
                <div className="screenshot-caption-sub">Custom numpad, one tap category</div>
              </div>
            </div>
            <div className="screenshot-item">
              <InsightsMockup />
              <div>
                <div className="screenshot-caption-title">Insights + Streak</div>
                <div className="screenshot-caption-sub">Weekly chart, daily streak count</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────────────── */}
      <section className="section" style={{ background: 'var(--bg-alt)' }}>
        <div className="container">
          <div className="features-intro fade-up">
            <div className="section-label">Testimonials</div>
            <h2 className="text-headline">Students who stopped wondering where their money went.</h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((t, i) => (
              <div key={t.name} className={`testimonial-card fade-up delay-${i + 1}`}>
                <div className="testimonial-stars">
                  {Array.from({ length: t.stars }).map((_, j) => <span key={j} className="star">★</span>)}
                </div>
                <p className="testimonial-text">"{t.quote}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar" style={{ background: t.avatarColor }}>{t.initials}</div>
                  <div>
                    <div className="testimonial-name">{t.name}</div>
                    <div className="testimonial-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="features-intro fade-up">
            <div className="section-label">FAQ</div>
            <h2 className="text-headline">Common questions, honest answers.</h2>
          </div>
          <div className="faq-list fade-up delay-2">
            {homeFAQ.map(item => (
              <FAQItem key={item.q} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ───────────────────────────────────────────────────── */}
      <div className="cta-banner">
        <div className="container">
          <div className="cta-banner-content">
            <h2 className="cta-banner-headline">Start tracking today. It's free.</h2>
            <p className="cta-banner-sub">
              Download Klar and join the students who already know exactly where their money goes every month.
            </p>
            <StoreButtons className="center" size="lg" />
          </div>
        </div>
      </div>
    </>
  );
}
